import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useSEO } from "../hooks/useSEO";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { api } from "../lib/api";
import { useReveal } from "../hooks/useReveal";

const schema = z.object({
  company: z.string().min(2, "Requis").max(100),
  first_name: z.string().min(2, "Requis").max(50),
  last_name: z.string().min(2, "Requis").max(50),
  email: z.string().email("Email invalide"),
  phone: z.string().max(20).optional().or(z.literal("")),
  position: z.string().min(2, "Requis").max(100),
  message: z.string().min(20, "20 caractères minimum").max(2000),
  website: z.string().max(0).default(""),
});

const PILLARS = [
  {
    roman: "I",
    title: "Une institution en fondation",
    body: "Le Stand d'Or naît en 2026 comme la première cérémonie officielle dédiée à l'humour francophone. Être partenaire fondateur, c'est inscrire votre marque dans l'acte de naissance d'une institution culturelle — avant qu'elle ne devienne une référence.",
  },
  {
    roman: "II",
    title: "Une audience de prescripteurs",
    body: "Notre communauté est composée d'amateurs éclairés, de professionnels du spectacle vivant, de journalistes et de créateurs. Une démographie premium, engagée, difficile à atteindre par les canaux traditionnels.",
  },
  {
    roman: "III",
    title: "Un cadre éditorial exigeant",
    body: "Chaque partenariat est intégré avec soin dans notre univers visuel et éditorial. Pas de bannières. Pas de pop-ups. Une présence cohérente avec l'identité d'une cérémonie de prestige.",
  },
];

export default function Partenaires() {
  useSEO(
    "Partenariat",
    "Associez votre marque à la première institution des awards de l'humour francophone. Nous contacter pour une collaboration sur-mesure."
  );

  const [sent, setSent] = useState(false);
  const [serverError, setServerError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const heroRef = useReveal();
  const pillarsRef = useReveal();
  const quoteRef = useReveal();
  const formRef = useReveal();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { website: "" },
  });

  const onSubmit = async (data) => {
    setSubmitting(true);
    setServerError(null);
    try {
      await api.post("/partner-inquiry", { ...data, package_type: "custom" });
      setSent(true);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Une erreur est survenue. Veuillez réessayer.";
      setServerError(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = {
    background: "transparent",
    border: "none",
    borderBottom: "1px solid #3a3a3a",
    color: "#F8F6F0",
    fontSize: 16,
    padding: "12px 0",
    width: "100%",
    outline: "none",
    fontFamily: "inherit",
  };

  const labelStyle = {
    fontSize: 11,
    letterSpacing: "0.2em",
    textTransform: "uppercase",
    color: "#A1A1AA",
    display: "block",
    marginBottom: 6,
  };

  const errorStyle = {
    fontSize: 12,
    color: "#C9A961",
    marginTop: 4,
  };

  return (
    <div data-testid="partenaires-page">
      {/* HERO */}
      <section
        ref={heroRef}
        className="reveal relative overflow-hidden grain"
        style={{ minHeight: "60vh", background: "#000", display: "flex", alignItems: "flex-end" }}
      >
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(160deg, rgba(201,169,97,0.07) 0%, rgba(0,0,0,0) 60%)",
          }}
          aria-hidden="true"
        />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 pt-[180px] pb-24 w-full">
          <p className="label mb-8" style={{ color: "#C9A961" }}>
            Partenariat officiel · Édition MMXXVI
          </p>
          <h1
            className="font-serif"
            style={{
              fontSize: "clamp(52px, 10vw, 140px)",
              lineHeight: 0.9,
              letterSpacing: "-0.03em",
              color: "#F8F6F0",
            }}
          >
            Associez-vous
            <br />
            <em style={{ color: "#C9A961" }}>à l'histoire.</em>
          </h1>
          <p
            className="font-serif mt-10"
            style={{
              color: "rgba(248,246,240,0.65)",
              fontSize: "clamp(18px, 2vw, 24px)",
              maxWidth: 580,
              lineHeight: 1.6,
            }}
          >
            Le Stand d'Or est la première institution de reconnaissance du
            stand-up francophone. Un nombre restreint de partenaires fondateurs
            sera associé à cette première édition.
          </p>
        </div>
      </section>

      <div className="hairline max-w-[1400px] mx-auto" />

      {/* PILLARS */}
      <section
        ref={pillarsRef}
        className="reveal max-w-[1400px] mx-auto px-6 lg:px-12 py-32 md:py-48"
      >
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-24">
          <div className="md:col-span-5">
            <p className="label mb-6" style={{ color: "#C9A961" }}>
              Pourquoi nous rejoindre
            </p>
            <h2
              className="font-serif"
              style={{
                fontSize: "clamp(36px, 4.5vw, 64px)",
                lineHeight: 1.04,
                color: "#F8F6F0",
              }}
            >
              Être là dès le premier acte.
            </h2>
          </div>
          <div
            className="md:col-span-5 md:col-start-8 flex items-end"
            style={{ color: "rgba(248,246,240,0.65)", fontSize: 17, lineHeight: 1.75 }}
          >
            <p>
              Les institutions naissent une fois. Cannes existait avant d'être
              Cannes. Le Ballon d'Or avant d'être le Ballon d'Or. Nous
              construisons, pour l'humour francophone, ce que ces cérémonies ont
              bâti pour le cinéma et le football.
            </p>
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: 1,
            background: "#1c1c1c",
          }}
        >
          {PILLARS.map((p) => (
            <div
              key={p.roman}
              style={{
                background: "#0A0A0A",
                padding: "48px 36px",
              }}
            >
              <p
                className="font-serif"
                style={{
                  fontSize: 13,
                  letterSpacing: "0.25em",
                  color: "#C9A961",
                  marginBottom: 24,
                }}
              >
                {p.roman}
              </p>
              <h3
                className="font-serif"
                style={{
                  fontSize: 22,
                  color: "#F8F6F0",
                  lineHeight: 1.2,
                  marginBottom: 20,
                }}
              >
                {p.title}
              </h3>
              <p
                style={{
                  fontSize: 15,
                  color: "rgba(248,246,240,0.62)",
                  lineHeight: 1.75,
                }}
              >
                {p.body}
              </p>
            </div>
          ))}
        </div>
      </section>

      <div className="hairline max-w-[1400px] mx-auto" />

      {/* QUOTE */}
      <section
        ref={quoteRef}
        className="reveal max-w-[1400px] mx-auto px-6 lg:px-12 py-24 md:py-40"
      >
        <div style={{ maxWidth: 860 }}>
          <p
            className="font-serif"
            style={{
              fontSize: "clamp(28px, 3.5vw, 52px)",
              color: "#F8F6F0",
              lineHeight: 1.25,
              letterSpacing: "-0.01em",
            }}
          >
            « L'humour entre dans sa légende. Les partenaires qui nous
            rejoignent aujourd'hui en seront les premiers témoins —
            et les premiers signataires. »
          </p>
          <p
            className="label mt-10"
            style={{ color: "#C9A961" }}
          >
            Le Stand d'Or, Édition MMXXVI
          </p>
        </div>
      </section>

      <div className="hairline max-w-[1400px] mx-auto" />

      {/* FORM */}
      <section
        ref={formRef}
        className="reveal max-w-[1400px] mx-auto px-6 lg:px-12 py-32 md:py-48"
        data-testid="partenaires-form-section"
      >
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-20">
          <div className="md:col-span-5">
            <p className="label mb-6" style={{ color: "#C9A961" }}>
              Prise de contact
            </p>
            <h2
              className="font-serif"
              style={{
                fontSize: "clamp(36px, 4.5vw, 64px)",
                lineHeight: 1.04,
                color: "#F8F6F0",
              }}
            >
              Déposez votre dossier.
            </h2>
          </div>
          <div
            className="md:col-span-5 md:col-start-8 flex items-end"
            style={{ color: "rgba(248,246,240,0.65)", fontSize: 16, lineHeight: 1.75 }}
          >
            <p>
              Chaque demande est étudiée avec soin. Décrivez votre marque, vos
              objectifs, et la nature de votre intérêt — nous vous recontactons
              sous 48 heures ouvrées pour discuter d'une collaboration
              sur-mesure.
            </p>
          </div>
        </div>

        {sent ? (
          <div
            style={{
              maxWidth: 640,
              padding: "48px 40px",
              border: "1px solid #1c1c1c",
              background: "#0A0A0A",
            }}
            data-testid="partenaires-success"
          >
            <p className="label mb-4" style={{ color: "#C9A961" }}>
              Demande reçue
            </p>
            <p
              className="font-serif"
              style={{ fontSize: 28, color: "#F8F6F0", lineHeight: 1.2 }}
            >
              Nous vous répondrons dans les 48 heures.
            </p>
            <p
              style={{
                color: "rgba(248,246,240,0.55)",
                fontSize: 15,
                lineHeight: 1.7,
                marginTop: 20,
              }}
            >
              Un accusé de réception vous a été envoyé à l'adresse indiquée.
            </p>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            data-testid="partenaires-form"
            style={{ maxWidth: 860 }}
          >
            {/* Honeypot */}
            <input
              {...register("website")}
              type="text"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              style={{ display: "none" }}
            />

            {/* Name row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              <div>
                <label style={labelStyle}>Prénom *</label>
                <input
                  {...register("first_name")}
                  type="text"
                  autoComplete="given-name"
                  style={inputStyle}
                  placeholder="Marie"
                />
                {errors.first_name && (
                  <p style={errorStyle}>{errors.first_name.message}</p>
                )}
              </div>
              <div>
                <label style={labelStyle}>Nom *</label>
                <input
                  {...register("last_name")}
                  type="text"
                  autoComplete="family-name"
                  style={inputStyle}
                  placeholder="Dupont"
                />
                {errors.last_name && (
                  <p style={errorStyle}>{errors.last_name.message}</p>
                )}
              </div>
            </div>

            {/* Company + position */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              <div>
                <label style={labelStyle}>Entreprise *</label>
                <input
                  {...register("company")}
                  type="text"
                  autoComplete="organization"
                  style={inputStyle}
                  placeholder="Acme Corp"
                />
                {errors.company && (
                  <p style={errorStyle}>{errors.company.message}</p>
                )}
              </div>
              <div>
                <label style={labelStyle}>Fonction *</label>
                <input
                  {...register("position")}
                  type="text"
                  autoComplete="organization-title"
                  style={inputStyle}
                  placeholder="Directeur marketing"
                />
                {errors.position && (
                  <p style={errorStyle}>{errors.position.message}</p>
                )}
              </div>
            </div>

            {/* Email + phone */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              <div>
                <label style={labelStyle}>Email professionnel *</label>
                <input
                  {...register("email")}
                  type="email"
                  autoComplete="email"
                  style={inputStyle}
                  placeholder="marie@acme.fr"
                />
                {errors.email && (
                  <p style={errorStyle}>{errors.email.message}</p>
                )}
              </div>
              <div>
                <label style={labelStyle}>Téléphone</label>
                <input
                  {...register("phone")}
                  type="tel"
                  autoComplete="tel"
                  style={inputStyle}
                  placeholder="+33 6 00 00 00 00"
                />
                {errors.phone && (
                  <p style={errorStyle}>{errors.phone.message}</p>
                )}
              </div>
            </div>

            {/* Message */}
            <div style={{ marginBottom: 40 }}>
              <label style={labelStyle}>
                Votre démarche * — objectifs, contexte, attentes
              </label>
              <textarea
                {...register("message")}
                rows={6}
                style={{
                  ...inputStyle,
                  borderBottom: "none",
                  border: "1px solid #3a3a3a",
                  padding: "16px",
                  resize: "vertical",
                  minHeight: 160,
                }}
                placeholder="Présentez votre marque, vos objectifs de partenariat, et toute demande spécifique. Plus votre message est précis, plus notre réponse sera adaptée."
              />
              {errors.message && (
                <p style={errorStyle}>{errors.message.message}</p>
              )}
            </div>

            {serverError && (
              <p
                style={{
                  color: "#C9A961",
                  fontSize: 14,
                  marginBottom: 24,
                  padding: "12px 16px",
                  border: "1px solid #C9A961",
                }}
                role="alert"
              >
                {serverError}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="btn-ghost"
              style={{ opacity: submitting ? 0.5 : 1, cursor: submitting ? "default" : "pointer" }}
              data-testid="partenaires-submit"
            >
              {submitting ? "Envoi en cours…" : "Envoyer la demande →"}
            </button>

            <p
              style={{
                marginTop: 24,
                fontSize: 12,
                color: "rgba(248,246,240,0.3)",
                lineHeight: 1.6,
              }}
            >
              Vos données sont utilisées exclusivement dans le cadre de ce
              partenariat et ne sont pas transmises à des tiers.
            </p>
          </form>
        )}
      </section>
    </div>
  );
}
